/**
 * DepartmentPicker - Wrapper around generic EntityPicker
 * 
 * Provides department-specific configuration and maintains backward compatibility
 * with any code that imports DepartmentPickerField or DepartmentPickerModal
 */

import React from 'react';
import { EntityPickerField } from '../EntityPicker/EntityPickerField';
import { EntityPickerModal } from '../EntityPicker/EntityPickerModal';
import { EntityPickerSelect } from '../EntityPicker/EntityPickerSelect';
import { FEATURES } from '../../../config/features';

// ── Department-specific configuration ────────────────────────────────────────
const getDepartmentConfig = (divisionId, employeeTypeId) => {
  const params = new URLSearchParams();

  // Check global flags for API query params
  if (FEATURES.USE_DIM_DIVISION_DEPARTMENT) params.append("includeDivision", "true");
  if (FEATURES.USE_DIM_EMPLOYEETYPE_DEPARTMENT) params.append("includeEmployeeType", "true");

  // Attach the filters if they exist
  if (divisionId) params.append("divisionId", divisionId);
  if (employeeTypeId) params.append("employeeTypeId", employeeTypeId);

  const queryStr = params.toString();
  const apiEndpoint = `/api/Department/active${queryStr ? `?${queryStr}` : ''}`;

  const config = {
    apiEndpoint: apiEndpoint,
    entityName: "Department",
    idField: "id",
    displayFields: {
      primary: FEATURES.DEPARTMENT_CODE ? "code" : "description",
      secondary: FEATURES.DEPARTMENT_CODE ? "description" : "code",
    },
    tableHeaders: ["Code", "Description"],
    tableAccessors: ["code", "description"],
    searchFields: [
      { label: "All", field: "all", index: 0 },
      { label: "Description", field: "description", index: 1 },
      { label: "Code", field: "code", index: 2 },
    ],
    placeholder: "--Select department--"
  };

  // ADDED: Auto-inject Division columns if feature enabled
  if (FEATURES.USE_DIM_DIVISION_DEPARTMENT) {
    config.tableHeaders.push("Division");
    config.tableAccessors.push("divisionDescription");
    config.searchFields.push({
      label: "Division",
      field: "divisionDescription",
      index: config.searchFields.length
    });
  }

  // ADDED: Auto-inject Employee Type columns if feature enabled
  if (FEATURES.USE_DIM_EMPLOYEETYPE_DEPARTMENT) {
    config.tableHeaders.push("Emp. Type");
    config.tableAccessors.push("employeeTypeDescription");
    config.searchFields.push({
      label: "Emp. Type",
      field: "employeeTypeDescription",
      index: config.searchFields.length
    });
  }

  return config;
};

// REPLACED: Original constant with the result of the function for backward compatibility
export const DEPARTMENT_CONFIG = getDepartmentConfig();

// ── DepartmentPickerSelect wrapper ───────────────────────────────────────────
/**
 * DepartmentPickerSelect
 * 
 * Usage:
 * <DepartmentPickerSelect
 *   label="Department"
 *   departmentId={form.departmentId}
 *   onSelect={(val) => setDepartment(val)}
 *   placeholder="All Departments"
 * />
 */
export function DepartmentPickerSelect({
  label,
  divisionId,     // division-specific prop name (if applicable)
  employeeTypeId, // employee type-specific prop name (if applicable) 
  value,   // Only ID is required for display/matching
  onSelect,
  onClear,
  placeholder = "--Select department--",
  allowAll,
  disabled = false,
  isRequired = false,
  className = "",
  error = false,
  errorMessage = ""
}) {
  const config = getDepartmentConfig(divisionId, employeeTypeId);
  return (
    <div className="mb-1 w-full">
      {label && (
        <label className="mb-1.5 block text-sm font-medium text-gray-600">
          {label}
          {isRequired && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}

      <EntityPickerSelect
        config={config}
        // Pass an object with the ID. 
        // EntityPickerSelect matches this against its internal list.
        value={value ? { id: value } : null}
        disabled={disabled}
        placeholder={placeholder}
        allowAll={allowAll}
        className={className}
        onChange={(val) => {
          if (val === null) {
            if (onClear) onClear();
          } else {
            // Returns the full object {id, code, description}
            if (onSelect) onSelect(val);
          }
        }}
      />

      {/* Error message display */}
      {error && (
        <span className="mt-1 block text-sm text-red-500">
          {errorMessage}
        </span>
      )}
    </div>
  );
}

// ── DepartmentPickerField wrapper ────────────────────────────────────────────
/**
 * DepartmentPickerField
 * 
 * Usage:
 * <DepartmentPickerField
 *   label="Department"
 *   departmentId={form.departmentId}
 *   displayValue={form.departmentDescription}
 *   onSelect={(department) => {
 *     set("departmentId", department.id);
 *     set("departmentCode", department.code);
 *     set("departmentDescription", department.description);
 *   }}
 *   onClear={() => {
 *     set("departmentId", "");
 *     set("departmentCode", "");
 *     set("departmentDescription", "");
 *   }}
 *   disabled={!editable}
 * />
 */
export function DepartmentPickerField({
  departmentId,       // department-specific prop name
  divisionId,     // division-specific prop name (if applicable)
  employeeTypeId, // employee type-specific prop name (if applicable) 
  displayValue = "",
  onSelect,
  onClear,
  ...rest             // label, isRequired, error, disabled, etc.
}) {
  const config = getDepartmentConfig(divisionId, employeeTypeId);
  return (
    <EntityPickerField
      {...rest}
      config={config}
      entityId={departmentId}
      displayValue={displayValue}
      onSelect={onSelect}
      onClear={onClear}
    />
  );
}

// ── DepartmentPickerModal wrapper ────────────────────────────────────────────
/**
 * DepartmentPickerModal
 * 
 * Usage (standalone):
 * <DepartmentPickerModal
 *   isOpen={showModal}
 *   onClose={() => setShowModal(false)}
 *   setData={(department) => {
 *     set("departmentId", department.id);
 *     set("departmentDescription", department.description);
 *   }}
 * />
 */
export function DepartmentPickerModal({ isOpen, onClose, setData }) {
  return (
    <EntityPickerModal
      isOpen={isOpen}
      onClose={onClose}
      setData={setData}
      config={DEPARTMENT_CONFIG}
    />
  );
}

// ── Exports (backward compatibility) ─────────────────────────────────────────
export { DepartmentPickerField as default };